import React, { type PropsWithChildren } from 'react';
import {
  Platform,
  Pressable,
  StyleSheet,
  type StyleProp,
  type ViewStyle,
} from 'react-native';

import { spacing } from '../../core/theme/tokens';
import { GlassSurface } from './glass';

type GlassCardProps = PropsWithChildren<{
  style?: StyleProp<ViewStyle>;
  onPress?: () => void;
  accessibilityLabel?: string;
}>;

export function GlassCard({
  children,
  style,
  onPress,
  accessibilityLabel,
}: GlassCardProps) {
  const card = (
    <GlassSurface
      tint="neutral"
      rounded="lg"
      showHairline
      // Padding/gap must live on the inner content layer — styles on the shell
      // don't space children (they're wrapped) and stretch oddly in equal-height grids.
      contentStyle={[styles.card, style]}
    >
      {children}
    </GlassSurface>
  );

  if (!onPress) {
    return card;
  }

  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      style={({ pressed }) => [pressed && styles.pressed, styles.pressable]}
    >
      {card}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  card: {
    padding: Platform.OS === 'web' ? spacing.md : spacing.lg,
  },
  pressable: {
    // web: clickable affordance without layout shift
    cursor: 'pointer',
  } as ViewStyle,
  pressed: {
    opacity: 0.88,
  },
});
